import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Loader2, Save, X } from 'lucide-react'
import api from '@/lib/api'

interface Service {
  id: number
  code: string
  name: string
  description: string | null
  category: string | null
  estimated_hours: number
  is_active: boolean
  sort_order: number
}

const CATEGORIES: Record<string, string> = {
  general: 'General', motherboard: 'Placa base', power: 'Carga', screen: 'Pantalla',
  keyboard: 'Teclado', hinge: 'Bisagras', casing: 'Carcasa', battery: 'Batería',
}

export default function RepairServicesTab() {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ code: '', name: '', description: '', category: 'general', estimated_hours: 1, is_active: true, sort_order: 0 })
  const [saving, setSaving] = useState(false)

  useEffect(() => { loadServices() }, [])

  const loadServices = async () => {
    setLoading(true)
    try {
      const res = await api.get('/repairs/admin/services')
      setServices(res.data)
    } catch (err) {
      console.error('Error loading services:', err)
    } finally {
      setLoading(false)
    }
  }

  const startEdit = (s: Service) => {
    setEditingId(s.id)
    setCreating(false)
    setForm({ code: s.code, name: s.name, description: s.description || '', category: s.category || 'general', estimated_hours: s.estimated_hours, is_active: s.is_active, sort_order: s.sort_order })
  }

  const startCreate = () => {
    setCreating(true)
    setEditingId(null)
    setForm({ code: '', name: '', description: '', category: 'general', estimated_hours: 1, is_active: true, sort_order: 0 })
  }

  const cancel = () => {
    setEditingId(null)
    setCreating(false)
  }

  const saveService = async () => {
    setSaving(true)
    try {
      if (creating) {
        await api.post('/repairs/admin/services', form)
      } else if (editingId) {
        await api.put(`/repairs/admin/services/${editingId}`, form)
      }
      cancel()
      loadServices()
    } catch (err) {
      console.error('Error saving service:', err)
    } finally {
      setSaving(false)
    }
  }

  const deleteService = async (id: number) => {
    if (!confirm('¿Eliminar este servicio?')) return
    try {
      await api.delete(`/repairs/admin/services/${id}`)
      loadServices()
    } catch (err) {
      console.error('Error deleting service:', err)
    }
  }

  const inputClass = "w-full px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"

  if (loading) return <div className="p-6 text-center text-slate-400">Cargando servicios...</div>

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Catálogo de servicios de reparación</h3>
        <button onClick={startCreate} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">
          <Plus className="w-4 h-4" /> Nuevo servicio
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr className="text-left text-slate-500 dark:text-slate-400">
              <th className="px-4 py-3 font-medium">Código</th>
              <th className="px-4 py-3 font-medium">Nombre</th>
              <th className="px-4 py-3 font-medium">Categoría</th>
              <th className="px-4 py-3 font-medium">Horas est.</th>
              <th className="px-4 py-3 font-medium">Activo</th>
              <th className="px-4 py-3 font-medium w-24">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
            {/* Create row */}
            {creating && (
              <tr className="bg-indigo-50/50 dark:bg-indigo-950/20">
                <td className="px-4 py-2"><input value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder="SERV-XXX" className={inputClass} /></td>
                <td className="px-4 py-2"><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre del servicio" className={inputClass} /></td>
                <td className="px-4 py-2">
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className={inputClass}>
                    {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2"><input type="number" step="0.25" value={form.estimated_hours} onChange={e => setForm({...form, estimated_hours: parseFloat(e.target.value) || 0})} className={inputClass} /></td>
                <td className="px-4 py-2">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <button onClick={saveService} disabled={saving} className="p-1 text-emerald-600 hover:text-emerald-700"><Save className="w-4 h-4" /></button>
                    <button onClick={cancel} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            )}

            {services.map(s => (
              editingId === s.id ? (
                <tr key={s.id} className="bg-amber-50/50 dark:bg-amber-950/20">
                  <td className="px-4 py-2"><input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className={inputClass} /></td>
                  <td className="px-4 py-2"><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClass} /></td>
                  <td className="px-4 py-2">
                    <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className={inputClass}>
                      {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2"><input type="number" step="0.25" value={form.estimated_hours} onChange={e => setForm({...form, estimated_hours: parseFloat(e.target.value) || 0})} className={inputClass} /></td>
                  <td className="px-4 py-2"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" /></td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <button onClick={saveService} disabled={saving} className="p-1 text-emerald-600 hover:text-emerald-700"><Save className="w-4 h-4" /></button>
                      <button onClick={cancel} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-3 font-mono text-xs text-indigo-600 dark:text-indigo-400">{s.code}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{s.name}</td>
                  <td className="px-4 py-3 text-slate-500">{CATEGORIES[s.category || ''] || s.category}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{s.estimated_hours}h</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex w-2 h-2 rounded-full ${s.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(s)} className="p-1 text-slate-400 hover:text-indigo-600"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteService(s.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
